/**
 * emailMarketingApi
 *
 * DASH-EMAIL-012A: narrow client for the real Email Marketing read API
 * (EMAIL-CATALOG-007A, `/api/email-marketing/*`), replacing
 * emailCreativeAnalyticsMockAdapter.js as Creative Analytics' data source.
 *
 * Wraps the SAME shared `api` axios instance every other real-backed War
 * Room surface already uses (see lib/api.js, CanonicalCampaignAnalytics.jsx)
 * — no second HTTP stack, no separate auth/error-normalization mechanism.
 * Auth (Bearer token) and tenant scoping (X-Account-ID header) are attached
 * automatically by that instance's own request interceptor; every function
 * here is a thin `api.get(...)` call plus response-shape unwrapping.
 *
 * Only wraps endpoints Creative Analytics actually consumes today. Does
 * NOT wrap GET /programs standalone (superseded by
 * getEmailMarketingProgramHierarchy, needed to source the shared
 * Program/Campaign filter dropdown with real governed IDs — see that
 * function's own comment) or GET /audience-definitions (no current
 * Creative Analytics consumer).
 */
import { api } from "./api";

export async function getEmailMarketingCampaigns() {
  const res = await api.get("/email-marketing/campaigns");
  return res.data;
}

export async function getEmailMarketingAngles() {
  const res = await api.get("/email-marketing/angles");
  return res.data;
}

/**
 * getEmailMarketingProgramHierarchy
 *
 * Builds a Program -> Campaign hierarchy in the SAME shape
 * (`{id, label, campaigns: [{id, label, broadcasts: []}]}`) EmailFilterBar
 * already expects from its `programs` prop (previously always
 * emailPerformanceMockAdapter.js's PROGRAM_HIERARCHY, a synthetic
 * "prog-*"/"camp-*" id scheme entirely unrelated to the governed catalog).
 *
 * Wiring Creative Analytics' Program/Campaign filter to real governed IDs
 * requires this: passing PROGRAM_HIERARCHY's ids into
 * `getCreativeAnalyticsCreatives({program_id, campaign_id})` would silently
 * match zero rows forever (a real, material contract gap between the
 * shared filter bar and the real API discovered during 012A implementation
 * — see the 012A completion report). `broadcasts: []` on every Campaign is
 * intentional: the Creative Analytics filter field config has
 * `broadcast: false`, so the Broadcast select never renders for this view
 * and this field is simply unused here.
 */
export async function getEmailMarketingProgramHierarchy() {
  const [programsRes, campaignsRes] = await Promise.all([
    api.get("/email-marketing/programs"),
    api.get("/email-marketing/campaigns"),
  ]);
  const programs = programsRes.data || [];
  const campaigns = campaignsRes.data || [];
  return programs.map((p) => ({
    id: p.program_id,
    label: p.name,
    campaigns: campaigns
      .filter((c) => c.program_id === p.program_id)
      .map((c) => ({ id: c.campaign_id, label: c.name, broadcasts: [] })),
  }));
}

export async function getCreativeAnalyticsOverview() {
  const res = await api.get("/email-marketing/creative-analytics/overview");
  return res.data;
}

export async function getCreativeAnalyticsAngles() {
  const res = await api.get("/email-marketing/creative-analytics/angles");
  return res.data;
}

export async function getCreativeAnalyticsCreatives({ programId, campaignId, angleId, status } = {}) {
  const params = {};
  if (programId && programId !== "all") params.program_id = programId;
  if (campaignId && campaignId !== "all") params.campaign_id = campaignId;
  if (angleId && angleId !== "all") params.angle_id = angleId;
  if (status) params.status = status;
  const res = await api.get("/email-marketing/creative-analytics/creatives", { params });
  return res.data;
}

/** Returns null on 404 (creative not found / cross-tenant) rather than throwing — callers distinguish "not found" from a genuine request error by null vs. a rejected promise. */
export async function getCreativeDetail(creativeId) {
  try {
    const res = await api.get(`/email-marketing/creative-analytics/creatives/${encodeURIComponent(creativeId)}`);
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** Returns null on 404 — see getCreativeDetail. */
export async function getCreativeVersionDetail(creativeId, version) {
  try {
    const res = await api.get(
      `/email-marketing/creative-analytics/creatives/${encodeURIComponent(creativeId)}/versions/${encodeURIComponent(version)}`
    );
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

export async function getCreativeAnalyticsAssets(family) {
  const res = await api.get(`/email-marketing/creative-analytics/assets/${encodeURIComponent(family)}`);
  return res.data;
}

/** Returns null on 404 — see getCreativeDetail. */
export async function getAssetVersionDetail(family, assetId, version) {
  try {
    const res = await api.get(
      `/email-marketing/creative-analytics/assets/${encodeURIComponent(family)}/${encodeURIComponent(assetId)}/versions/${encodeURIComponent(version)}`
    );
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

export async function getBroadcasts({ creativeId, campaignId, status, limit = 25, offset = 0 } = {}) {
  const params = { limit, offset };
  if (creativeId) params.creative_id = creativeId;
  if (campaignId && campaignId !== "all") params.campaign_id = campaignId;
  if (status) params.status = status;
  const res = await api.get("/email-marketing/broadcasts", { params });
  return res.data;
}

/** Returns null on 404 — see getCreativeDetail. */
export async function getBroadcastDetail(broadcastId) {
  try {
    const res = await api.get(`/email-marketing/broadcasts/${encodeURIComponent(broadcastId)}`);
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

export function extractApiErrorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}

// =============================================================================
// EMAILS-IMPL-007: Catalog authoring (EMAILS-IMPL-006, `/api/email-marketing/
// {programs,campaigns,angles,audience-definitions}` POST/PATCH). Extends this
// SAME client rather than creating a second Email Marketing networking layer.
// =============================================================================

/** Standalone flat Program list -- distinct from getEmailMarketingProgramHierarchy
 * above (which composes Program+Campaign into a nested filter-bar shape).
 * The Programs authoring panel needs the plain governed records themselves. */
export async function getEmailMarketingPrograms() {
  const res = await api.get("/email-marketing/programs");
  return res.data;
}

export async function getEmailMarketingAudienceDefinitions() {
  const res = await api.get("/email-marketing/audience-definitions");
  return res.data;
}

export async function createProgram({ name, status }) {
  const res = await api.post("/email-marketing/programs", { name, status });
  return res.data;
}

export async function updateProgram(programId, { name, status, version }) {
  const res = await api.patch(`/email-marketing/programs/${encodeURIComponent(programId)}`, { name, status, version });
  return res.data;
}

export async function createCampaign({ name, status, programId, angleId }) {
  const res = await api.post("/email-marketing/campaigns", {
    name,
    status,
    program_id: programId,
    angle_id: angleId || null,
  });
  return res.data;
}

export async function updateCampaign(campaignId, { name, status, angleId, version }) {
  const res = await api.patch(`/email-marketing/campaigns/${encodeURIComponent(campaignId)}`, {
    name,
    status,
    angle_id: angleId,
    version,
  });
  return res.data;
}

export async function createAngle({ name, status }) {
  const res = await api.post("/email-marketing/angles", { name, status });
  return res.data;
}

export async function updateAngle(angleId, { name, status, version }) {
  const res = await api.patch(`/email-marketing/angles/${encodeURIComponent(angleId)}`, { name, status, version });
  return res.data;
}

export async function createAudienceDefinition({ name, status }) {
  const res = await api.post("/email-marketing/audience-definitions", { name, status });
  return res.data;
}

export async function updateAudienceDefinition(audienceId, { name, status, version }) {
  const res = await api.patch(`/email-marketing/audience-definitions/${encodeURIComponent(audienceId)}`, {
    name,
    status,
    version,
  });
  return res.data;
}

// =============================================================================
// EMAILS-IMPL-009 (mutations) + EMAILS-IMPL-009A (reads): Asset authoring
// + catalog read (EMAILS-IMPL-008/009A,
// `/api/email-marketing/{subjects,previews,hooks,email-bodies,offers,ctas,
// ps,landing-pages,images}`). Extends this SAME client rather than
// creating a second Asset networking layer, mirroring the EMAILS-IMPL-007
// Catalog-authoring section above.
//
// EMAILS-IMPL-009A adds getX()/getXVersions() for all 9 families, backed
// by the new `emails`-gated GET routes on the SAME router as the existing
// POST/PATCH below (email_marketing_asset_authoring.py) -- NOT the
// pre-existing War-Room-gated `GET /creative-analytics/assets/{family}`
// analytics rollup (AssetAnalyticsRollupItem carries creative_version_
// count/broadcast_count/channel2_metrics), which remains untouched and
// unused here. This closes the gap the EMAILS-IMPL-009 completion report
// flagged as an Open Issue: the Asset catalog is no longer session-only.
// =============================================================================

export async function createSubject({ name, status, canonicalSubject }) {
  const res = await api.post("/email-marketing/subjects", { name, status, canonical_subject: canonicalSubject });
  return res.data;
}

export async function createSubjectVersion(subjectId, { version, canonicalSubject }) {
  const res = await api.post(`/email-marketing/subjects/${encodeURIComponent(subjectId)}/versions`, {
    version,
    canonical_subject: canonicalSubject,
  });
  return res.data;
}

export async function updateSubjectMetadata(subjectId, { name, status }) {
  const res = await api.patch(`/email-marketing/subjects/${encodeURIComponent(subjectId)}`, { name, status });
  return res.data;
}

export async function createPreview({ name, status, canonicalPreview }) {
  const res = await api.post("/email-marketing/previews", { name, status, canonical_preview: canonicalPreview });
  return res.data;
}

export async function createPreviewVersion(previewId, { version, canonicalPreview }) {
  const res = await api.post(`/email-marketing/previews/${encodeURIComponent(previewId)}/versions`, {
    version,
    canonical_preview: canonicalPreview,
  });
  return res.data;
}

export async function updatePreviewMetadata(previewId, { name, status }) {
  const res = await api.patch(`/email-marketing/previews/${encodeURIComponent(previewId)}`, { name, status });
  return res.data;
}

export async function createHook({ name, status, canonicalHook }) {
  const res = await api.post("/email-marketing/hooks", { name, status, canonical_hook: canonicalHook });
  return res.data;
}

export async function createHookVersion(hookId, { version, canonicalHook }) {
  const res = await api.post(`/email-marketing/hooks/${encodeURIComponent(hookId)}/versions`, {
    version,
    canonical_hook: canonicalHook,
  });
  return res.data;
}

export async function updateHookMetadata(hookId, { name, status }) {
  const res = await api.patch(`/email-marketing/hooks/${encodeURIComponent(hookId)}`, { name, status });
  return res.data;
}

export async function createEmailBody({ name, status, canonicalEmailBody }) {
  const res = await api.post("/email-marketing/email-bodies", { name, status, canonical_email_body: canonicalEmailBody });
  return res.data;
}

export async function createEmailBodyVersion(emailBodyId, { version, canonicalEmailBody }) {
  const res = await api.post(`/email-marketing/email-bodies/${encodeURIComponent(emailBodyId)}/versions`, {
    version,
    canonical_email_body: canonicalEmailBody,
  });
  return res.data;
}

export async function updateEmailBodyMetadata(emailBodyId, { name, status }) {
  const res = await api.patch(`/email-marketing/email-bodies/${encodeURIComponent(emailBodyId)}`, { name, status });
  return res.data;
}

export async function createOffer({ name, status, canonicalOffer }) {
  const res = await api.post("/email-marketing/offers", { name, status, canonical_offer: canonicalOffer });
  return res.data;
}

export async function createOfferVersion(offerId, { version, canonicalOffer }) {
  const res = await api.post(`/email-marketing/offers/${encodeURIComponent(offerId)}/versions`, {
    version,
    canonical_offer: canonicalOffer,
  });
  return res.data;
}

export async function updateOfferMetadata(offerId, { name, status }) {
  const res = await api.patch(`/email-marketing/offers/${encodeURIComponent(offerId)}`, { name, status });
  return res.data;
}

export async function createPs({ name, status, canonicalPs }) {
  const res = await api.post("/email-marketing/ps", { name, status, canonical_ps: canonicalPs });
  return res.data;
}

export async function createPsVersion(psId, { version, canonicalPs }) {
  const res = await api.post(`/email-marketing/ps/${encodeURIComponent(psId)}/versions`, {
    version,
    canonical_ps: canonicalPs,
  });
  return res.data;
}

export async function updatePsMetadata(psId, { name, status }) {
  const res = await api.patch(`/email-marketing/ps/${encodeURIComponent(psId)}`, { name, status });
  return res.data;
}

// EMAILS-IMPL-032 (EMAILS-SPEC-017/017B)
export async function createCsm({ name, status, canonicalCsm }) {
  const res = await api.post("/email-marketing/cta-support-microcopy", { name, status, canonical_csm: canonicalCsm });
  return res.data;
}

export async function createCsmVersion(csmId, { version, canonicalCsm }) {
  const res = await api.post(`/email-marketing/cta-support-microcopy/${encodeURIComponent(csmId)}/versions`, {
    version,
    canonical_csm: canonicalCsm,
  });
  return res.data;
}

export async function updateCsmMetadata(csmId, { name, status }) {
  const res = await api.patch(`/email-marketing/cta-support-microcopy/${encodeURIComponent(csmId)}`, { name, status });
  return res.data;
}

export async function createRin({ name, status, canonicalRin }) {
  const res = await api.post("/email-marketing/reply-instructions", { name, status, canonical_rin: canonicalRin });
  return res.data;
}

export async function createRinVersion(rinId, { version, canonicalRin }) {
  const res = await api.post(`/email-marketing/reply-instructions/${encodeURIComponent(rinId)}/versions`, {
    version,
    canonical_rin: canonicalRin,
  });
  return res.data;
}

export async function updateRinMetadata(rinId, { name, status }) {
  const res = await api.patch(`/email-marketing/reply-instructions/${encodeURIComponent(rinId)}`, { name, status });
  return res.data;
}

/** cta_fingerprint is sent as-is (an object of the 5 CTA-SPEC-001 §11 fields, or null/undefined) -- never fabricated here. */
export async function createCta({ name, status, canonicalCta, ctaFingerprint }) {
  const res = await api.post("/email-marketing/ctas", {
    name,
    status,
    canonical_cta: canonicalCta,
    cta_fingerprint: ctaFingerprint || null,
  });
  return res.data;
}

export async function createCtaVersion(ctaId, { version, canonicalCta, ctaFingerprint }) {
  const res = await api.post(`/email-marketing/ctas/${encodeURIComponent(ctaId)}/versions`, {
    version,
    canonical_cta: canonicalCta,
    cta_fingerprint: ctaFingerprint || null,
  });
  return res.data;
}

export async function updateCtaMetadata(ctaId, { name, status }) {
  const res = await api.patch(`/email-marketing/ctas/${encodeURIComponent(ctaId)}`, { name, status });
  return res.data;
}

/** internalLandingPageId/internalLandingPageVersion are optional and independent -- see LandingPagesPanel.jsx for the "both or neither" UX. */
export async function createLandingPage({ name, status, internalLandingPageId, internalLandingPageVersion }) {
  const res = await api.post("/email-marketing/landing-pages", {
    name,
    status,
    internal_landing_page_id: internalLandingPageId || null,
    internal_landing_page_version: internalLandingPageVersion ?? null,
  });
  return res.data;
}

export async function createLandingPageVersion(landingPageId, { version, internalLandingPageId, internalLandingPageVersion }) {
  const res = await api.post(`/email-marketing/landing-pages/${encodeURIComponent(landingPageId)}/versions`, {
    version,
    internal_landing_page_id: internalLandingPageId || null,
    internal_landing_page_version: internalLandingPageVersion ?? null,
  });
  return res.data;
}

export async function updateLandingPageMetadata(landingPageId, { name, status }) {
  const res = await api.patch(`/email-marketing/landing-pages/${encodeURIComponent(landingPageId)}`, { name, status });
  return res.data;
}

/**
 * createImage / createImageVersion — multipart/form-data, matching the
 * merged EMAILS-IMPL-008 backend contract exactly (Form fields + an
 * UploadFile). `file` is the browser File object from an <input
 * type="file"> -- its `.type` becomes the request's real Content-Type
 * negotiation via axios's automatic FormData handling (see lib/api.js: the
 * shared `api` instance sets no default Content-Type, so axios detects the
 * FormData body and sets the correct multipart boundary itself). Nothing
 * here manually derives/guesses content_type, width, height, or file_size
 * -- the backend probes the actual bytes (email_marketing_image_service.
 * probe_image_bytes) and returns the authoritative values in the response.
 */
export async function createImage({ name, status, altText, file }) {
  const form = new FormData();
  form.append("name", name);
  form.append("status", status);
  form.append("alt_text", altText);
  form.append("file", file);
  const res = await api.post("/email-marketing/images", form);
  return res.data;
}

export async function createImageVersion(imageId, { version, altText, file }) {
  const form = new FormData();
  form.append("version", version);
  form.append("alt_text", altText);
  form.append("file", file);
  const res = await api.post(`/email-marketing/images/${encodeURIComponent(imageId)}/versions`, form);
  return res.data;
}

export async function updateImageMetadata(imageId, { name, status }) {
  const res = await api.patch(`/email-marketing/images/${encodeURIComponent(imageId)}`, { name, status });
  return res.data;
}

// =============================================================================
// EMAILS-IMPL-009A: Asset catalog reads. Each `get{Family}s()` hits the
// new `GET /email-marketing/{plural}` list route (every Asset merged with
// its own current Version -- never the full history); each
// `get{Family}Versions(id)` hits the paired `GET .../{id}/versions` route
// (read-only immutable Version history, most-recent-created first). Both
// are `emails: full`-gated, same router as the mutation functions above --
// never the War-Room-gated analytics rollup (see this section's own note
// above).
// =============================================================================

export async function getSubjects() {
  const res = await api.get("/email-marketing/subjects");
  return res.data;
}

export async function getSubjectVersions(subjectId) {
  const res = await api.get(`/email-marketing/subjects/${encodeURIComponent(subjectId)}/versions`);
  return res.data;
}

export async function getPreviews() {
  const res = await api.get("/email-marketing/previews");
  return res.data;
}

export async function getPreviewVersions(previewId) {
  const res = await api.get(`/email-marketing/previews/${encodeURIComponent(previewId)}/versions`);
  return res.data;
}

export async function getHooks() {
  const res = await api.get("/email-marketing/hooks");
  return res.data;
}

export async function getHookVersions(hookId) {
  const res = await api.get(`/email-marketing/hooks/${encodeURIComponent(hookId)}/versions`);
  return res.data;
}

export async function getEmailBodies() {
  const res = await api.get("/email-marketing/email-bodies");
  return res.data;
}

export async function getEmailBodyVersions(emailBodyId) {
  const res = await api.get(`/email-marketing/email-bodies/${encodeURIComponent(emailBodyId)}/versions`);
  return res.data;
}

export async function getOffers() {
  const res = await api.get("/email-marketing/offers");
  return res.data;
}

export async function getOfferVersions(offerId) {
  const res = await api.get(`/email-marketing/offers/${encodeURIComponent(offerId)}/versions`);
  return res.data;
}

export async function getCtas() {
  const res = await api.get("/email-marketing/ctas");
  return res.data;
}

export async function getCtaVersions(ctaId) {
  const res = await api.get(`/email-marketing/ctas/${encodeURIComponent(ctaId)}/versions`);
  return res.data;
}

export async function getPsAssets() {
  const res = await api.get("/email-marketing/ps");
  return res.data;
}

export async function getPsVersions(psId) {
  const res = await api.get(`/email-marketing/ps/${encodeURIComponent(psId)}/versions`);
  return res.data;
}

// EMAILS-IMPL-032 (EMAILS-SPEC-017/017B)
export async function getCsmAssets() {
  const res = await api.get("/email-marketing/cta-support-microcopy");
  return res.data;
}

export async function getCsmVersions(csmId) {
  const res = await api.get(`/email-marketing/cta-support-microcopy/${encodeURIComponent(csmId)}/versions`);
  return res.data;
}

export async function getRinAssets() {
  const res = await api.get("/email-marketing/reply-instructions");
  return res.data;
}

export async function getRinVersions(rinId) {
  const res = await api.get(`/email-marketing/reply-instructions/${encodeURIComponent(rinId)}/versions`);
  return res.data;
}

export async function getLandingPages() {
  const res = await api.get("/email-marketing/landing-pages");
  return res.data;
}

export async function getLandingPageVersions(landingPageId) {
  const res = await api.get(`/email-marketing/landing-pages/${encodeURIComponent(landingPageId)}/versions`);
  return res.data;
}

export async function getImages() {
  const res = await api.get("/email-marketing/images");
  return res.data;
}

export async function getImageVersions(imageId) {
  const res = await api.get(`/email-marketing/images/${encodeURIComponent(imageId)}/versions`);
  return res.data;
}

// =============================================================================
// EMAILS-IMPL-011: Creative authoring + catalog reads
// (backend/app/routers/email_marketing_creative_authoring.py,
// EMAILS-IMPL-010). Extends this SAME client rather than creating a
// second Creative networking layer, mirroring every prior section above.
// Deliberately does NOT call `/email-marketing/creative-analytics/*` --
// that surface is the War-Room-gated analytics rollup
// (version_count/broadcast_count/channel2_metrics), a completely
// different concern from this operational, `emails: full`-gated catalog.
//
// `toCompositionPayload` is the single place that converts the frontend's
// per-slot `{assetId, version}` shape (camelCase, matching every other
// function in this file) into the backend's exact `{asset_id, version}`
// wire shape for all 9 Fingerprint slots -- every populated slot always
// carries BOTH fields; the backend rejects an asset_id-only reference, so
// this function never omits `version`.
// =============================================================================

function toSlotPayload(ref) {
  return ref ? { asset_id: ref.assetId, version: ref.version } : null;
}

function toCompositionPayload(composition) {
  return {
    subject: toSlotPayload(composition.subject),
    preview: toSlotPayload(composition.preview),
    hook: toSlotPayload(composition.hook),
    email_body: toSlotPayload(composition.emailBody),
    offer: toSlotPayload(composition.offer),
    cta: toSlotPayload(composition.cta),
    landing_page: toSlotPayload(composition.landingPage),
    ps: toSlotPayload(composition.ps),
    image: toSlotPayload(composition.image),
    image_presentation: composition.imagePresentation || null,
  };
}

// EMAILS-IMPL-015: converts the Email Builder's own camelCase Canvas Block
// draft shape ({blockId, family, assetId, version, role}) into the
// backend's exact CanvasBlock wire shape (CRE-SPEC-001 §15.4-§15.9,
// email_marketing_creatives.CanvasBlock). `canvasLayout` is optional --
// undefined/null maps to `null` (identical wire behavior to omitting the
// field entirely, since the backend's own field is `Optional[...] = None`)
// so every EXISTING caller that never passes canvasLayout continues to
// send a payload byte-for-byte equivalent to before this checkpoint.
function toCanvasBlockPayload(block) {
  return {
    block_id: block.blockId,
    family: block.family,
    asset_id: block.assetId,
    version: block.version,
    role: block.role || null,
  };
}

function toCanvasLayoutPayload(canvasLayout) {
  return canvasLayout ? canvasLayout.map(toCanvasBlockPayload) : null;
}

export async function getCreatives() {
  const res = await api.get("/email-marketing/creatives");
  return res.data;
}

export async function getCreative(creativeId) {
  const res = await api.get(`/email-marketing/creatives/${encodeURIComponent(creativeId)}`);
  return res.data;
}

export async function createCreative({ name, status, campaignId, angleId, channel, composition, canvasLayout }) {
  const res = await api.post("/email-marketing/creatives", {
    name,
    status,
    campaign_id: campaignId,
    angle_id: angleId || null,
    channel: channel || null,
    composition: toCompositionPayload(composition),
    canvas_layout: toCanvasLayoutPayload(canvasLayout),
  });
  return res.data;
}

export async function updateCreativeMetadata(creativeId, { name, status }) {
  const res = await api.patch(`/email-marketing/creatives/${encodeURIComponent(creativeId)}`, { name, status });
  return res.data;
}

export async function createCreativeVersion(creativeId, { version, composition, canvasLayout }) {
  const res = await api.post(`/email-marketing/creatives/${encodeURIComponent(creativeId)}/versions`, {
    version,
    composition: toCompositionPayload(composition),
    canvas_layout: toCanvasLayoutPayload(canvasLayout),
  });
  return res.data;
}

export async function getCreativeVersions(creativeId) {
  const res = await api.get(`/email-marketing/creatives/${encodeURIComponent(creativeId)}/versions`);
  return res.data;
}

// =============================================================================
// EMAILS-IMPL-020: Broadcast authoring + Rendered Handoff Snapshot
// generation (backend/app/routers/email_marketing_broadcast_authoring.py,
// EMAILS-IMPL-018/019/020A). Extends this SAME client rather than creating
// a second Broadcast networking layer.
//
// Deliberately named with an "Authoring" qualifier and hits the distinct
// `/broadcast-authoring` base path -- NOT `/email-marketing/broadcasts*`
// (getBroadcasts/getBroadcastDetail above), which is the pre-existing
// War-Room-gated analytics rollup at a different namespace (EMAILS-IMPL-018
// found that namespace already claimed the bare `/broadcasts` noun and real
// frontend code -- EmailCreativeVersionTable.jsx -- already depends on it,
// so the new operational, `emails: full`-gated authoring surface lives at
// its own base path instead; mirrored here so the two never collide or get
// confused client-side either).
// =============================================================================

// BRC0026-PAGINATION-FIX: returns the {items,total,limit,offset} envelope,
// not a bare array. The Broadcast authoring catalog is server-bounded and
// newest-first; before this fix the endpoint silently returned only the first
// 25 Broadcasts ascending, so the newest was permanently invisible. `limit`
// and `offset` are only sent when supplied, so the server's own defaults stay
// authoritative. Filters compose with pagination unchanged.
export async function getBroadcastAuthoringList({
  campaignId, creativeId, status, environment, limit, offset,
} = {}) {
  const params = {};
  if (campaignId) params.campaign_id = campaignId;
  if (creativeId) params.creative_id = creativeId;
  if (status) params.status = status;
  if (environment) params.environment = environment;
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  const res = await api.get("/email-marketing/broadcast-authoring", { params });
  return res.data;
}

export async function getBroadcastAuthoring(broadcastId) {
  const res = await api.get(`/email-marketing/broadcast-authoring/${encodeURIComponent(broadcastId)}`);
  return res.data;
}

export async function createBroadcastAuthoring({ name, campaignId, creativeId, creativeVersion, audienceDefinitionId, environment, senderConfigurationId, footerConfigurationId }) {
  const res = await api.post("/email-marketing/broadcast-authoring", {
    name,
    campaign_id: campaignId,
    creative_id: creativeId,
    creative_version: creativeVersion,
    audience_definition_id: audienceDefinitionId,
    environment,
    // EMAILS-IMPL-021: required for every new authored Broadcast -- an
    // operator selects an Approved Sender Configuration whose own
    // Environment matches this Broadcast's declared Environment; never a
    // free-text sender field.
    sender_configuration_id: senderConfigurationId,
    // EMAILS-IMPL-033: required for every new authored Broadcast -- an
    // operator selects an Approved Email Footer Configuration whose own
    // Environment matches this Broadcast's declared Environment, exactly
    // mirroring the Sender Configuration selection contract above.
    footer_configuration_id: footerConfigurationId,
  });
  return res.data;
}

/** PATCH is metadata-only (name/status) -- campaign/creative/version/audience/environment/sender_configuration_id are never sent, mirroring the backend's own immutable-configuration boundary (see BroadcastMetadataUpdateRequest). */
export async function updateBroadcastAuthoring(broadcastId, { name, status }) {
  const res = await api.patch(`/email-marketing/broadcast-authoring/${encodeURIComponent(broadcastId)}`, { name, status });
  return res.data;
}

// =============================================================================
// EMAILS-IMPL-021: Governed Sender Configuration authoring
// (backend/app/routers/email_marketing_sender_configuration_authoring.py,
// BRD-SPEC-001 §10.2). Extends this SAME client rather than creating a
// second Sender Configuration networking layer. A brand-new resource name
// (`/sender-configurations`) -- no route-namespace collision to work
// around, unlike Broadcast's own `/broadcast-authoring` decision.
// =============================================================================

export async function getSenderConfigurations({ environment, status } = {}) {
  const params = {};
  if (environment) params.environment = environment;
  if (status) params.status = status;
  const res = await api.get("/email-marketing/sender-configurations", { params });
  return res.data;
}

export async function getSenderConfiguration(senderConfigurationId) {
  const res = await api.get(`/email-marketing/sender-configurations/${encodeURIComponent(senderConfigurationId)}`);
  return res.data;
}

export async function createSenderConfiguration({ name, environment, fromName, fromAddress, replyTo }) {
  const res = await api.post("/email-marketing/sender-configurations", {
    name,
    environment,
    from_name: fromName,
    from_address: fromAddress,
    reply_to: replyTo,
  });
  return res.data;
}

/**
 * updateSenderConfiguration
 *
 * One PATCH endpoint serves three distinct operator actions, exactly
 * mirroring the backend's own single-endpoint-service-layer-enforced-
 * legality design (see email_marketing_sender_configuration_authoring_
 * service.py): editing a Draft's fields, approving it, and retiring an
 * Approved one. Callers pass only the fields relevant to their action;
 * the backend rejects anything illegal for the record's current status.
 */
export async function updateSenderConfiguration(senderConfigurationId, { name, environment, fromName, fromAddress, replyTo, status } = {}) {
  const res = await api.patch(`/email-marketing/sender-configurations/${encodeURIComponent(senderConfigurationId)}`, {
    name,
    environment,
    from_name: fromName,
    from_address: fromAddress,
    reply_to: replyTo,
    status,
  });
  return res.data;
}

// =============================================================================
// EMAILS-IMPL-033: Governed Email Footer Configuration authoring
// (backend/app/routers/email_marketing_footer_configuration_authoring.py,
// BRD-SPEC-001 §10.3). Mirrors the Sender Configuration client above exactly
// -- same lightweight, tenant-scoped, Environment-scoped, mutable-not-
// versioned, Draft/Approved/Retired lifecycle and single-PATCH-endpoint
// contract, at its own `/footer-configurations` resource name.
// =============================================================================

export async function getFooterConfigurations({ environment, status } = {}) {
  const params = {};
  if (environment) params.environment = environment;
  if (status) params.status = status;
  const res = await api.get("/email-marketing/footer-configurations", { params });
  return res.data;
}

export async function getFooterConfiguration(footerConfigurationId) {
  const res = await api.get(`/email-marketing/footer-configurations/${encodeURIComponent(footerConfigurationId)}`);
  return res.data;
}

export async function createFooterConfiguration({ name, environment, brandName, legalEntityName, copyrightText, mailingAddress, privacyPolicyUrl, termsOfUseUrl, complianceInstructionalText }) {
  const res = await api.post("/email-marketing/footer-configurations", {
    name,
    environment,
    brand_name: brandName,
    legal_entity_name: legalEntityName,
    copyright_text: copyrightText,
    mailing_address: mailingAddress,
    privacy_policy_url: privacyPolicyUrl,
    terms_of_use_url: termsOfUseUrl,
    compliance_instructional_text: complianceInstructionalText,
  });
  return res.data;
}

/**
 * updateFooterConfiguration
 *
 * One PATCH endpoint serves three distinct operator actions, exactly
 * mirroring updateSenderConfiguration above: editing a Draft's fields,
 * approving it, and retiring an Approved one. Callers pass only the fields
 * relevant to their action; the backend rejects anything illegal for the
 * record's current status.
 */
export async function updateFooterConfiguration(footerConfigurationId, { name, environment, brandName, legalEntityName, copyrightText, mailingAddress, privacyPolicyUrl, termsOfUseUrl, complianceInstructionalText, status } = {}) {
  const res = await api.patch(`/email-marketing/footer-configurations/${encodeURIComponent(footerConfigurationId)}`, {
    name,
    environment,
    brand_name: brandName,
    legal_entity_name: legalEntityName,
    copyright_text: copyrightText,
    mailing_address: mailingAddress,
    privacy_policy_url: privacyPolicyUrl,
    terms_of_use_url: termsOfUseUrl,
    compliance_instructional_text: complianceInstructionalText,
    status,
  });
  return res.data;
}

/**
 * generateBroadcastHandoffSnapshot
 *
 * POST .../{broadcast_id}/handoff-snapshot -- generate-once, immutable
 * (BRD-SPEC-001 §13.1). Takes no body: the backend derives everything from
 * the Broadcast's own already-persisted governed identity. A second call
 * against a Broadcast that already has a Snapshot returns 409 (structured
 * the same plain-`detail`-string way as every other non-title-collision
 * error here) -- callers use describeMutationError() same as everywhere
 * else in this file.
 */
export async function generateBroadcastHandoffSnapshot(broadcastId) {
  const res = await api.post(`/email-marketing/broadcast-authoring/${encodeURIComponent(broadcastId)}/handoff-snapshot`);
  return res.data;
}

/**
 * describeMutationError
 *
 * EMAILS-IMPL-006's authoring routes return a STRUCTURED 409 body for a
 * title collision (`{error:"title_collision", message, family, existing_id,
 * existing_title, existing_status}` -- see
 * email_marketing_catalog_authoring_api.py's TitleCollisionErrorBody /
 * email_marketing_catalog_authoring.py's `_title_collision_detail`), and a
 * plain string `detail` for every other error (404 not-found, 422
 * relationship/no-op-update, 409 immutable-field conflict). This function is
 * the single place that tells the two apart so no panel component has to
 * re-derive the distinction, and never surfaces a raw stack trace or Mongo
 * error -- the server's message is always authoritative; this only shapes
 * it for display.
 *
 * Returns { kind: "title_collision" | "message", message, collision? }.
 * `collision` (only on "title_collision") carries the safe fields for a UI
 * that wants to link to the existing record.
 */
export function describeMutationError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.error === "title_collision") {
    return {
      kind: "title_collision",
      message: `"${detail.existing_title}" already exists as ${detail.existing_id}.`,
      collision: {
        family: detail.family,
        existingId: detail.existing_id,
        existingTitle: detail.existing_title,
        existingStatus: detail.existing_status,
      },
    };
  }
  if (typeof detail === "string" && detail.trim()) {
    return { kind: "message", message: detail };
  }
  return { kind: "message", message: err?.message || fallback || "Something went wrong." };
}
